import styled from '@emotion/styled'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Colors from '../../../figma/panda/Colors'
import Spacings from '../../../figma/tokens/Spacings'
import FigmaBox from '../../../mynt-components/components/FigmaBox'
import { formatAmount } from '../../../tiger/libs/Common'
import { usePisDepositEvents, usePisDepositInspection } from '../api/usePisDeposits'
import { bankName, FAILURE_REASON_LABELS, formatInstant, PENDING_REASON_LABELS, type PisDeposit, reasonLabel } from '../types'
import { PisDepositStatusChip } from './PisDepositStatusChip'

type PisDepositDetailPanelProps = {
  customerId: string
  deposit: PisDeposit
}

export const PisDepositDetailPanel = ({ customerId, deposit }: PisDepositDetailPanelProps) => {
  const { data: events, isLoading: eventsLoading } = usePisDepositEvents(customerId, deposit.depositId)
  const inspection = usePisDepositInspection(customerId, deposit.depositId)

  const reason = reasonLabel(deposit.failureReason, FAILURE_REASON_LABELS) ?? reasonLabel(deposit.pendingReason, PENDING_REASON_LABELS)

  return (
    <DetailWrapper fullWidth direction="row" gap={Spacings.max}>
      <FigmaBox>
        <SectionTitle>General</SectionTitle>
        <InfoRow label="Deposit ID" value={deposit.depositId} />
        <InfoRow label="Created" value={formatInstant(deposit.createdAt, 'YYYY-MM-DD HH:mm:ss')} />
        <InfoRow label="Amount" value={deposit.amount ? formatAmount(deposit.amount) : undefined} />
        <InfoRow label="Bank" value={deposit.bank ? `${bankName(deposit.bank)} (${deposit.bank})` : undefined} />
        <InfoRow label="OCR" value={deposit.paymentReference} />
        <InfoRow label="Provider payment ID" value={deposit.providerPaymentId} />
        <InfoRow label="Reason" value={reason} />
      </FigmaBox>

      <FigmaBox>
        <SectionTitle>Status history</SectionTitle>
        {eventsLoading && <CircularProgress size={20} />}
        {events && events.length === 0 && <Typography variant="body2">No recorded events</Typography>}
        {events && events.length > 0 && (
          <Table size="small" sx={{ width: 'auto', '& .MuiTableCell-root': { fontSize: 13, py: 0.5 } }}>
            <TableHead>
              <TableRow>
                <HeaderCell>Observed at</HeaderCell>
                <HeaderCell>Status</HeaderCell>
                <HeaderCell>Reason</HeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event, index) => {
                const eventReason =
                  reasonLabel(event.failureReason, FAILURE_REASON_LABELS) ?? reasonLabel(event.pendingReason, PENDING_REASON_LABELS)
                return (
                  <TableRow key={`${event.observedAt}-${index}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatInstant(event.observedAt, 'YYYY-MM-DD HH:mm:ss')}</TableCell>
                    <TableCell>
                      <PisDepositStatusChip status={event.status} />
                    </TableCell>
                    <TableCell>{eventReason ?? '-'}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </FigmaBox>

      <FigmaBox>
        <SectionTitle>Live status</SectionTitle>
        <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: Colors.base500, mb: 1, maxWidth: 320 }}>
          Fetches the payment’s current status directly from the bank and corrects the stored state if it differs.
        </Typography>
        <Box>
          <Button variant="contained" size="small" onClick={() => inspection.mutate()} disabled={inspection.isLoading}>
            {inspection.isLoading ? 'Checking with bank…' : 'Check live status at bank'}
          </Button>
        </Box>
        {inspection.isError && (
          <Typography variant="body2" sx={{ color: Colors.notificationErrors600, mt: 1 }}>
            Live status check failed — the bank or the open banking provider may be unavailable.
          </Typography>
        )}
        {inspection.data && !inspection.isLoading && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Status at bank:
              </Typography>
              <PisDepositStatusChip status={inspection.data.status} />
            </Box>
            {inspection.data.providerDetail && (
              <Box
                component="pre"
                sx={{
                  p: 1.5,
                  backgroundColor: Colors.base200,
                  borderRadius: 1,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  m: 0,
                  maxWidth: 480,
                  maxHeight: 320,
                  overflowY: 'auto',
                }}
              >
                {inspection.data.providerDetail}
              </Box>
            )}
          </Box>
        )}
      </FigmaBox>
    </DetailWrapper>
  )
}

const InfoRow = ({ label, value }: { label: string; value?: string }) => (
  <Row direction="row" gap={Spacings.tiny}>
    <Label>{label}:</Label>
    <span>{value || '-'}</span>
  </Row>
)

const DetailWrapper = styled(FigmaBox)`
  padding: ${Spacings.small} ${Spacings.medium};
  border-bottom: 1px solid #e0e0e0;
  background-color: ${Colors.base100};
  font-size: 13px;
`

const SectionTitle = styled.h4`
  margin: ${Spacings.small} 0 ${Spacings.tiny} 0;
  font-size: 14px;
  font-weight: 600;

  &:first-of-type {
    margin-top: 0;
  }
`

const HeaderCell = styled(TableCell)`
  font-weight: 600;
`

const Label = styled.span`
  font-weight: 600;
  min-width: 160px;
`

const Row = styled(FigmaBox)`
  margin-bottom: ${Spacings.minimum};
`
